import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { CookieService } from 'ngx-cookie-service';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ApiService } from '../../../../app/core/services/api.service';
import { db } from '../../../../app/core/services/db/db.service';
import { AccountService } from '../../../../app/views/account/account.service';
import { AppConstants } from '../../../../config/app-constants';
import { externalMsalInstance } from '../../../../config/ciam-msal-instance';
import { HeaderConfig, HeaderDetails } from '../../../../config/header-config';
import { ACCOUNTS_API_ENDPOINTS, CLAIM_API_ENDPOINTS } from '../../../../endpoints.const';
import { TREO_HEADER_TITLE, TREO_SUB_HEADER_TITLE } from '../../../../treo.properties';
import { ExcelService } from '../../../core/services/excel.service';
import { LoaderService } from '../../../core/services/loading.service';
import { SharedService } from '../../../core/services/shared.service';
import { ClaimsService } from '../../../views/claims/claims.service';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    standalone: false
})
export class HeaderComponent implements OnInit, OnDestroy {
    // example w/more options: https://github.thehartford.com/HIS/cto_ngkit/blob/master/src/app/app.component.ts
    treoMainTitle = TREO_HEADER_TITLE;
    treoSubTitle = TREO_SUB_HEADER_TITLE;
    public JSON = JSON;
    headerConfig: HeaderConfig = {
        primaryNavLeft: [
            {
                label: ''
            }
        ],
        primaryNavRight: [
            {
                label: 'Alerts',
                icon: '',
                subHeaderDetails: [
                    {
                        label: 'Notifications',
                        link: '/notifications'
                    },
                    {
                        label: 'Announcements',
                        link: '/announcements'
                    }
                ]
            },
            {
                label: 'Help',
                subHeaderDetails: [
                    {
                        label: 'Contact Us',
                        link: '/contact'
                    },
                    {
                        label: 'TREO Help',
                        link: '/toolsAndTrainingTab'
                    },
                    {
                        label: 'Other Resources',
                        link: '/toolsAndTrainingTab'
                    }
                ]
            },
            {
                label: 'Account Settings',
                link: '#',
                subHeaderDetails: [
                    {
                        label: 'Manage Account Team',
                        link: '/accountteamsearchresults'
                    },
                    {
                        label: 'Logout',
                        link: '/login'
                    }
                ]
            }
        ]
    };

    EditProfileDetails: HeaderDetails = {
        label: 'Edit Profile',
        link: '/profile-update'
    };
    changeAccDetails: HeaderDetails = {
        label: 'Change Account',
        link: '/landing-home'
    };
    accountTeam: HeaderDetails = {
        label: 'Account Team',
        link: '/accountteam'
    };
    manageAlerts: HeaderDetails = {
        label: 'Manage Alerts',
        link: '/notifications'
    };
    accountHomeTitle = '';
    showMobileMenuOptions = false;
    mobileDropDown: string;
    accountId: any;
    listOfMenuData: any;
    accountNameSubscription$: Subscription;
    menuSubscription$: Subscription;
    orgnztnIdSubscription$: Subscription;
    navigationSubscription$: Subscription;
    routingSubscription$: Subscription;
    routeSubscription$: Subscription;
    paramsObj: any;
    notificationCount: any;
    showAlertIcon: boolean = false;

    notificationCountInside: any;
    orgnztnId = '';
    programNvigableLinks: any;
    externalUserCheck: any;
    onLandingPage = true;
    private isLoggingOut = false; // Flag to prevent duplicate logout calls
    private externalLogoutStarted = false; // Guard to prevent duplicate external logout API invocation
    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private SharedService: SharedService,
        private excelService: ExcelService,
        public apiService: ApiService,
        private cookieService: CookieService,
        private authService: MsalService,
        private loaderService: LoaderService,
        public claimsService: ClaimsService,
        private accountService: AccountService
    ) {}
    /**
     * Define a global logout function that handles MSAL redirect logout for both internal and external users.
     * @param capturedUserType - The external user type captured before cleanup (optional)
     */
    private setGlobalLogout(): void {
        (window as any).logout = async (capturedUserType?: string | null) => {
            const externalUser: string | boolean = this.loaderService.getLocalItem('externalUser');
            const isExternalUser = externalUser === true || externalUser === 'true';

            try {
                if (isExternalUser) {
                    // await this.performExternalMsalLogout();
                    this.revokeExternalAccessToken();
                } else {
                    await this.performInternalMsalLogout();
                }
                this.router.navigate(['/login']);
            } catch (e) {
                console.error('Global logout failed', e);
            }
        };
    }

    /**
     * Perform MSAL logout redirect for external users (CIAM) reusing shared instance.
     */
    private async performExternalMsalLogout(): Promise<void> {
        let instance = externalMsalInstance;
        if (!instance) {
            console.error(
                '[Header Logout] externalMsalInstance missing; aborting MSAL redirect. Navigate to login after local cleanup.'
            );
            this.cleanupAfterLogout();
            this.router.navigate(['/login']);
            return;
        }
        const activeAccount = instance?.getActiveAccount();
        await this.clearAllMsalAccounts();
        try {
            await instance?.logoutRedirect({
                account: activeAccount,
                idTokenHint: this.loaderService.getLocalItem('idToken'),
                postLogoutRedirectUri: `${window.location.origin}/login`
            });
        } catch (e) {
            console.error('[Header Logout] logoutRedirect failed', e);
        }
        this.cleanupAfterLogout();
    }

    /**
     * Perform MSAL logout redirect for internal users (Azure AD)
     */
    private async performInternalMsalLogout(): Promise<void> {
        const accounts = this.authService.instance.getAllAccounts();
        console.log('Internal user accounts:', accounts);

        await this.clearAllMsalAccounts();
        await this.authService.logoutRedirect({
            postLogoutRedirectUri: `${window.location.origin}/login`,
            idTokenHint: this.authService.instance.getActiveAccount()?.idToken,
            onRedirectNavigate: () => false
        });

        this.cleanupAfterLogout();
    }

    /**
     * Clean up session and storage after logout
     */
    private cleanupAfterLogout(): void {
        this.loaderService.setLocalItem('externalUser', false);
        sessionStorage.clear();
    }

    /**
     * Remove all MSAL accounts from cache (fallback when token cache clear() not available).
     */
    private async clearAllMsalAccounts(): Promise<void> {
        // In this MSAL version the public ITokenCache interface does not expose enumeration/removal APIs.
        // Fallback: rely on logoutRedirect to clear the cache for the specified account. No-op here.
        return Promise.resolve();
    }

    /**
     * Listen for global logout events set in localStorage to propagate logout across duplicated tabs.
     */
    private initCrossTabLogoutListener(): void {
        window.addEventListener('storage', (e: StorageEvent) => {
            if (e.key === 'globalLogout' && e.newValue) {
                // Avoid double navigation if already on login
                if (!location.pathname.includes('/login')) {
                    // Only perform local cleanup and navigation, do NOT call externalLogout API
                    this.performLocalLogoutCleanup();
                    // Optionally, you can add a log for clarity
                    console.log('[Logout] Cross-tab event: performed local cleanup, skipped backend API call');
                }
            }
        });
    }

    /**
     * Common cleanup logic after logout (clear session-only data, selected local keys, and navigate).
     */
    private performLocalLogoutCleanup(redirect: boolean = true): void {
        try {
            this.cookieService.delete('CIAMSession');
            this.cookieService.deleteAll();
        } catch {
            /* ignore cookie errors */
        }
        // Clear tab-specific session storage
        // Remove sensitive localStorage keys but keep the globalLogout marker so other tabs can read it.
        const keysToRemove = ['userId', 'accountName', 'orgId', 'appAccountDetailId', 'navigableLinks'];
        keysToRemove.forEach((k) => localStorage.removeItem(k));
        if (redirect) {
            this.router.navigate(['/login']);
        }
    }
    setAccountDet(event) {
        this.accountHomeTitle = event.accountName;
        this.loaderService.removeItem('accountName');
        this.loaderService.removeLocalItem('accountName');
        this.loaderService.setItem('accountName', event.accountName);
        this.loaderService.setLocalItem('accountName', event.accountName);
        this.loaderService.removeItem('orgId');
        this.loaderService.removeItem('appAccountDetailId');
        this.loaderService.removeItem('visitedClaims');
        this.loaderService.setItem('orgId', event.organizationId);
        this.loaderService.setItem('organizationIdentifier', event.organizationIdentifier);
        this.loaderService.setItem('appAccountDetailId', event.appAccountDetailId);
        this.fetchHeaderTabs(event.organizationId, event);
        this.fetchPolicyList(event.organizationId);
        this.fetchNotificationCount(event.organizationId);
    }

    looksLikeJsonString(str) {
        return typeof str === 'string' && str.startsWith('"') && str.endsWith('"');
    }

    ngOnInit() {
        this.setGlobalLogout();
        // Cross-tab logout disabled per user request
        // this.initCrossTabLogoutListener();
        this.SharedService.alertCount$?.subscribe((count) => {
            this.notificationCount = count;
            this.showAlertIcon = this.notificationCount > 0;
        });

        this.SharedService.alertCountInside$?.subscribe((count) => {
            if (
                this.loaderService.getItem('accountNotVisitedCount') &&
                Number(this.loaderService.getItem('accountNotVisitedCount')) > 0
            ) {
                this.notificationCount = count;
                this.showAlertIcon = this.notificationCount > 0;
            }
        });

        this.routeSubscription$ = this.route.queryParams.subscribe((params) => {
            if (params['navigateFrom'] === 'Manage Alerts') {
                this.alertMail = params['alertAccountName'];
                this.alertNavigate = 'Manage Alerts';
            } else {
                this.alertMail = '';
                this.alertNavigate = '';
            }

            if (params['id'] && params['emailID'] && params['accountNm']) {
                this.paramsObj = {
                    email: params['emailID'],
                    id: params['id'],
                    organizationId: params['organizationId'],
                    appAccountDetailId: params['applicationAccntDtlId'] ? params['applicationAccntDtlId'] : null,
                    accountName: params['accountNm']
                };
                if (this.loaderService.getLocalItem('userId') != null) {
                    if (
                        this.loaderService.getLocalItem('emailID').toLowerCase().trim() ==
                        params['emailID'].toLowerCase().trim()
                    ) {
                        if (this.loaderService.getLocalItem('externalUser')) {
                            for (let i = 0; i < localStorage.length; i++) {
                                const key = localStorage.key(i);
                                if (key) {
                                    const value = localStorage.getItem(key);
                                    if (value !== null) {
                                        this.loaderService.setItem(key, value);
                                    }
                                }
                            }
                        }
                        this.accountHomeTitle = params['accountNm'];
                        this.orgnztnId = params['organizationId'];
                        if (location.pathname.includes('notifications')) {
                            this.SharedService.alertAcceptReject = true;
                            this.SharedService.alertParamsObj = this.paramsObj;
                            this.loaderService.setItem('AlertsTab', 'alerts');
                        }
                        if (location.pathname.includes('reports')) {
                            this.SharedService.reportAcceptReject = true;
                            this.SharedService.reportParamsObj = this.paramsObj;
                            this.loaderService.setItem('reportTab', 'My Reports Library');
                        }
                        if (this.looksLikeJsonString(this.loaderService.getLocalItem('userId'))) {
                            const cleanUserId = JSON.parse(this.loaderService.getLocalItem('userId'));
                            this.loaderService.setItem('userId', cleanUserId);
                            this.loaderService.setLocalItem('userId', cleanUserId);
                        } else {
                            this.loaderService.setItem('userId', this.loaderService.getLocalItem('userId'));
                            this.loaderService.setLocalItem('userId', this.loaderService.getLocalItem('userId'));
                        }
                        this.setAccountDet(this.paramsObj);
                    } else {
                        if (location.pathname.includes('dashboard-management')) {
                            this.SharedService.dashboardAcceptReject = true;
                            this.SharedService.dashboardParamsObj = this.paramsObj;
                        } else if (location.pathname.includes('panel-management')) {
                            this.SharedService.panelAcceptReject = true;
                            this.SharedService.panelParamsObj = this.paramsObj;
                        } else if (location.pathname.includes('notifications')) {
                            this.SharedService.alertAcceptReject = true;
                            this.SharedService.alertParamsObj = this.paramsObj;
                            this.loaderService.setItem('AlertsTab', 'alerts');
                        } else if (location.pathname.includes('reports')) {
                            this.SharedService.reportAcceptReject = true;
                            this.SharedService.reportParamsObj = this.paramsObj;
                            this.loaderService.setItem('reportTab', 'My Reports Library');
                        } else {
                            this.SharedService.filterAcceptReject = true;
                            this.SharedService.filterParamsObj = this.paramsObj;
                        }
                        this.router.navigate(['/login']);
                    }
                } else {
                    if (location.pathname.includes('dashboard-management')) {
                        this.SharedService.dashboardAcceptReject = true;
                        this.SharedService.dashboardParamsObj = this.paramsObj;
                    } else if (location.pathname.includes('panel-management')) {
                        this.SharedService.panelAcceptReject = true;
                        this.SharedService.panelParamsObj = this.paramsObj;
                    } else if (location.pathname.includes('notifications')) {
                        this.SharedService.alertAcceptReject = true;
                        this.SharedService.alertParamsObj = this.paramsObj;
                        this.loaderService.setItem('AlertsTab', 'alerts');
                    } else if (location.pathname.includes('reports')) {
                        this.SharedService.reportAcceptReject = true;
                        this.SharedService.reportParamsObj = this.paramsObj;
                        this.loaderService.setItem('reportTab', 'My Reports Library');
                    } else {
                        this.SharedService.filterAcceptReject = true;
                        this.SharedService.filterParamsObj = this.paramsObj;
                    }
                    this.router.navigate(['/login']);
                }
                const UserCheck = this.loaderService.getItem('externalUser');
                this.externalUserCheck = UserCheck;
                this.loaderService.setItem('orgId', params['organizationId']);
                this.loaderService.setItem(
                    'appAccountDetailId',
                    params['applicationAccntDtlId'] ? params['applicationAccntDtlId'] : null
                );
                this.loaderService.setItem('accountName', params['accountNm']);
                this.loaderService.setLocalItem('accountName', params['accountNm']);
                this.markAccountAsVisited();
            } else if (params['id'] && params['emailID']) {
                let orgIdValue = '';
                if (this.loaderService.getItem('orgId')) {
                    orgIdValue = this.loaderService.getItem('orgId');
                } else {
                    orgIdValue = params['organizationId'];
                }

                this.markAccountAsVisited();
            }
        });

        this.accountNameSubscription$ = this.SharedService.currentStatus?.subscribe((res: string) => {
            if (res !== '') {
                this.accountHomeTitle = res;
                this.headerConfig.primaryNavLeft[0].label = this.accountHomeTitle;
            } else {
                if (
                    this.loaderService.getItem('accountName') == null ||
                    this.loaderService.getItem('accountName') == 'null' ||
                    this.loaderService.getItem('accountName') == ''
                ) {
                    this.accountHomeTitle = '';
                } else if (
                    this.loaderService.getLocalItem('accountName') !== null &&
                    this.loaderService.getLocalItem('accountName') !== 'null' &&
                    this.loaderService.getItem('accountName') !== ''
                ) {
                    this.accountHomeTitle = this.loaderService.getLocalItem('accountName');
                } else {
                    this.accountHomeTitle = this.loaderService.getItem('accountName');
                }
            }
        });
        this.orgnztnIdSubscription$ = this.SharedService.currentNumber?.subscribe((res: string) => {
            if (res) {
                this.orgnztnId = res;
            }
        });
        this.menuSubscription$ = this.SharedService.headerMenuData?.subscribe((res: any) => {
            if (res != '') {
                this.listOfMenuData = res;
            } else {
                if (
                    this.listOfMenuData?.length === 0 ||
                    this.listOfMenuData == '' ||
                    this.listOfMenuData == undefined
                ) {
                    if (sessionStorage.getItem('navigableLinks')) {
                        this.listOfMenuData = JSON.parse(sessionStorage.getItem('navigableLinks'));
                    } else {
                        this.listOfMenuData = [];
                    }
                }
            }
            this.headerConfig.secondryNav = this.listOfMenuData;
            this.headerLinkToggle();
        });
        this.routingSubscription$ = this.router.events
            ?.pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe((routeData: any) => {
                if (!location.pathname.includes('login')) {
                    this.headerConfig = {
                        primaryNavLeft: [
                            {
                                label: ''
                            }
                        ],
                        primaryNavRight: [
                            {
                                label: 'Alerts',
                                icon: '',
                                subHeaderDetails: [
                                    {
                                        label: 'Notifications',
                                        link: '/notifications'
                                    },
                                    {
                                        label: 'Announcements',
                                        link: '/announcements'
                                    }
                                ]
                            },
                            {
                                label: 'Help',
                                subHeaderDetails: [
                                    {
                                        label: 'Contact Us',
                                        link: '/contact'
                                    },
                                    {
                                        label: 'TREO Help',
                                        link: '/toolsAndTrainingTab'
                                    },
                                    {
                                        label: 'Other Resources',
                                        link: '/toolsAndTrainingTab'
                                    }
                                ]
                            },
                            {
                                label: 'Account Settings',
                                link: '#',
                                subHeaderDetails: [
                                    {
                                        label: 'Manage Account Team',
                                        link: '/accountteamsearchresults'
                                    },
                                    {
                                        label: 'Logout',
                                        link: '#'
                                    }
                                ]
                            }
                        ]
                    };
                }
                if (
                    location.pathname.includes('login') ||
                    location.pathname.includes('sim-login') ||
                    location.pathname.includes('no-cav-access') ||
                    location.pathname.includes('expired-user') ||
                    location.pathname.includes('print-dashboard') ||
                    location.pathname.includes('auth') ||
                    location.pathname.includes('report-preview') ||
                    location.pathname.includes('reports-preview')
                ) {
                    this.headerConfig = {
                        primaryNavLeft: [
                            {
                                label: ''
                            }
                        ],
                        primaryNavRight: []
                    };
                }
                if (location.pathname.includes('profile-update')) {
                    this.headerConfig = {
                        primaryNavLeft: [
                            {
                                label: ''
                            }
                        ],
                        primaryNavRight: [
                            {
                                label: 'Alerts',
                                icon: '',
                                subHeaderDetails: [
                                    {
                                        label: 'Notifications',
                                        link: '/notifications'
                                    },
                                    {
                                        label: 'Announcements',
                                        link: '/announcements'
                                    }
                                ]
                            },
                            {
                                label: 'Help',
                                subHeaderDetails: [
                                    {
                                        label: 'Contact Us',
                                        link: '/contact'
                                    },
                                    {
                                        label: 'TREO Help',
                                        link: '/toolsAndTrainingTab'
                                    },
                                    {
                                        label: 'Other Resources',
                                        link: '/toolsAndTrainingTab'
                                    }
                                ]
                            },
                            {
                                label: 'Account Settings',
                                link: '#',
                                subHeaderDetails: [
                                    {
                                        label: 'Manage Account Team',
                                        link: '/accountteamsearchresults'
                                    },
                                    {
                                        label: 'Logout',
                                        link: '#'
                                    }
                                ]
                            }
                        ],
                        secondryNav: []
                    };
                }
                if (location.pathname.includes('landing-home')) {
                    this.onLandingPage = true;
                } else {
                    this.onLandingPage = false;
                }
                this.headerLinkToggleIfBlock(routeData);
            });

        const UserCheck = this.loaderService.getItem('externalUser');
        this.externalUserCheck = UserCheck;
    }

    headerTabsSubscription$: Subscription;
    fetchHeaderTabs(orgIdArray, params) {
        let userId = this.loaderService.getLocalItem('userId');
        let url = '';
        if (this.loaderService.getItem('appAccountDetailId') === '0') {
            url = `${ACCOUNTS_API_ENDPOINTS.SEARCH}?accountIdentifier=${orgIdArray}&accountIdentifierType=HeaderMenu&userId=${userId}`;
        } else if (params) {
            url = `${ACCOUNTS_API_ENDPOINTS.SEARCH}?accountIdentifier=${orgIdArray}&accountIdentifierOption=0&accountIdentifierType=HeaderMenu&userId=${userId}`;
        } else {
            url = `${
                ACCOUNTS_API_ENDPOINTS.SEARCH
            }?accountIdentifier=${orgIdArray}&accountIdentifierOption=${this.loaderService.getItem(
                'appAccountDetailId'
            )}&accountIdentifierType=HeaderMenu&userId=${userId}`;
        }
        let payload = {
            accountIdentifier: orgIdArray,
            accountIdentifierType: 'HeaderMenu',
            accountIdentifierOption: this.loaderService.getItem('appAccountDetailId') || 0
        };
        this.headerTabsSubscription$ = this.apiService.getData1(url, payload).subscribe({
            next: (resp) => {
                this.loaderService.hideLoader();
                if (resp.accounts[0].navigableLinks.length > 0) {
                    this.loaderService.setItem('userTrackingId', resp.userTrackingId);
                    this.SharedService.setHeaderMenuData(resp.accounts[0].navigableLinks);
                    this.listOfMenuData = resp.accounts[0].navigableLinks;
                    this.loaderService.setItem('navigableLinks', resp.accounts[0].navigableLinks);
                    this.loaderService.setItem('hasPrintDetails', resp.accounts[0]?.hasPrintDetails);
                    this.SharedService.storeOCDandTPAdetails({
                        isOCDAccount: resp.accounts[0].isOCDAccount,
                        isTPAAccount: resp.accounts[0].isTPAAccount
                    });
                    this.loaderService.setItem(
                        'valuationDate',
                        JSON.stringify({
                            firstValuationDate: resp.accounts[0].firstValuationDate,
                            lastDefaultValuationDate: resp.accounts[0].valuationDate
                        })
                    );
                    this.SharedService.storeValuationDates({
                        firstValuationDate: resp.accounts[0].firstValuationDate,
                        lastDefaultValuationDate: resp.accounts[0].valuationDate
                    });
                    this.headerLinkToggleElseBlock();
                    // if (location.pathname.includes("notifications")) { }
                    // this.reloadOnce()
                }
            }
        });
    }

    reloadOnce() {
        const hasReloaded = sessionStorage.getItem('hasReloaded');
        if (!hasReloaded) {
            sessionStorage.setItem('hasReloaded', 'true');
            // Guard against missing or non-function reload (e.g., JSDOM test env overrides window.location without reload)
            try {
                if (typeof location.reload === 'function') {
                    location.reload();
                }
            } catch (e) {
                // Swallow to avoid hard crashes in tests; real browsers will have reload.
                // console.debug('reloadOnce suppressed reload error', e);
            }
        }
    }

    isActive(instruction: string): boolean {
        let parentPath = instruction;
        if (instruction === '/dashboard/dashboard-management') {
            parentPath = '/dashboard';
        }
        if (instruction === '/osha') {
            parentPath = '/osha-home';
        }
        return this.router.isActive(parentPath, {
            paths: 'subset',
            queryParams: 'subset',
            fragment: 'ignored',
            matrixParams: 'ignored'
        });
    }

    /**
     * Handle user logout with duplicate call prevention and proper cleanup
     */
    private handleLogout(): void {
        // Prevent duplicate logout calls
        if (this.isLoggingOut) {
            console.log('Logout already in progress, ignoring duplicate call');
            return;
        }
        this.isLoggingOut = true;

        // Clear local database
        this.clearLocalDatabase();

        // Capture user type before cleanup destroys it
        const userType = this.loaderService.getItem('externalUser');
        const isExternalUser = userType === true || userType === 'true';

        // Cross-tab logout broadcast disabled per user request
        // localStorage.setItem('globalLogout', Date.now().toString());
        // IMPORTANT: For external users we must keep session cookies until the backend logout API runs.
        // So we defer performLocalLogoutCleanup() for external flow to after the API call succeeds/fails.
        if (isExternalUser) {
            this.performExternalUserLogout(userType);
        } else {
            // Internal user path can clean up immediately before MSAL logout
            this.performLocalLogoutCleanup(false);
            this.performInternalUserLogout(userType);
        }
    }

    /**
     * Clear local Dexie database and reset HOL flags
     */
    private clearLocalDatabase(): void {
        db.items.clear().then(() => {
            console.log('Dexie DB cleared');
            this.SharedService.setHOLDataFlag(false);
            this.loaderService.setItem('HOLApiCall', false);
        });
    }

    /**
     * Perform logout for external users (calls backend API first)
     */
    private performExternalUserLogout(userType: string | boolean): void {
        // Duplicate guard: if already started, skip second invocation.
        if (this.externalLogoutStarted) {
            console.warn('[Logout] performExternalUserLogout called again - skipping duplicate.');
            return;
        }
        this.externalLogoutStarted = true;

        // Instrumentation to trace source of invocation.
        console.log('[Logout] performExternalUserLogout invoked. Stack:', new Error().stack);

        const sub = this.apiService.externalLogout().subscribe({
            next: async () => {
                console.log('[Logout] externalLogout API success');
                // Now safe to clean local session data including cookies
                this.revokeExternalAccessToken();
                this.performLocalLogoutCleanup(false);
                this.loaderService.hideLoader();
                await this.triggerMsalLogout(userType);
                sub.unsubscribe();
            },
            error: async (err) => {
                console.error('[Logout] externalLogout API error (continuing with MSAL logout)', err);
                // Even if backend logout fails, still perform local cleanup so tab state resets
                this.performLocalLogoutCleanup(false);
                this.loaderService.hideLoader();
                await this.triggerMsalLogout(userType);
                sub.unsubscribe();
            }
        });
    }

    revokeExternalAccessToken(): void {
        const ciamUrl = `${AppConstants.APP_BASE_API_URL}token/revoke`;
        // const body = `token=${this.loaderService.getLocalItem('accessToken')}&token_type_hint=access_token&client_id=${AppConstants.GetEnvAuth.CIAMConfig.clientId}`;

        // const headers = new HttpHeaders({
        //     'Content-Type': 'application/x-www-form-urlencoded',
        //     // 'Skip-Auth': 'true'
        // });
        this.apiService.http.post(ciamUrl, '').subscribe({
            next: async () => {
                console.log('[Logout] CIAM token revoked successfully');
                await this.performExternalMsalLogout();
            },
            error: (err) => {
                console.error('[Logout] Failed to revoke CIAM token', err);
            }
        });
    }

    /**
     * Perform logout for internal users (MSAL only)
     */
    private performInternalUserLogout(userType: string | boolean): void {
        this.triggerMsalLogout(userType);
    }

    /**
     * Trigger MSAL logout redirect and handle errors
     */
    private async triggerMsalLogout(userType: string | boolean): Promise<void> {
        try {
            console.log('[Logout] triggerMsalLogout start');
            await (window as any).logout(userType);
            console.log('[Logout] triggerMsalLogout completed');
        } catch (e) {
            console.error('MSAL logout redirect failed', e);
            this.isLoggingOut = false; // Reset flag on error to allow retry
            this.externalLogoutStarted = false; // Allow retry of external API if needed
        }
    }

    /**
     * navigate to the given url
     * @param menu
     */
    navigateUrl(menu?: HeaderDetails) {
        if (location.pathname.includes('landing-home') && menu?.label === 'Edit Profile') {
            this.loaderService.setItem('prevRouteToProfile', 'landingHome');
            this.loaderService.setItem('firstTimeLogin', 'false');
        }
        if (location.pathname.includes('account-home') && menu?.label === 'Edit Profile') {
            this.loaderService.setItem('prevRouteToProfile', 'accountHome');
            this.loaderService.setItem('firstTimeLogin', 'false');
        }
        if (!menu?.link) {
            this.router.navigate(['/landing-home']);
            return;
        }
        const linkMappings: { [key: string]: string } = {
            OSHA: 'osha-home/osha-reporting',
            Osha: 'osha-home/osha-reporting',
            TPA: 'tpa/tpaActivityReport',
            tpa: 'tpa/tpaActivityReport'
        };
        if (menu.label in linkMappings) {
            this.router.navigate([linkMappings[menu.label]]);
        } else if (['HOME', 'Account Home', 'Home'].includes(menu.label)) {
            const orgId = JSON.parse(this.loaderService.getItem('orgId') || '""');
            this.navigationSubscription$ = this.SharedService.currentNumber.subscribe((res: any) => {
                if (res !== '') {
                    this.accountId = res;
                } else {
                    this.accountId = orgId;
                }
                this.router.navigate([`/account-home/${this.accountId}`]);
            });
        } else {
            const link = this.checkMenuLink(menu);
            if (link === '#' || link === '/login') {
                this.handleLogout();
                return;
            }
            if (menu.label.includes('Notifications')) {
                this.loaderService.setItem('AlertsTab', 'notifications');
                this.SharedService.setAlertsTab('notifications');
            }
            if (menu.label.includes('Manage Alerts')) {
                this.loaderService.setItem('AlertsTab', 'alerts');
                this.SharedService.setAlertsTab('alerts');
                setTimeout(() => {
                    // window.location.reload();
                }, 100);
            }
            if (menu.label.includes('TREO Help')) {
                this.loaderService.setItem('helpsTab', 'treo');
                this.SharedService.setHelpsTab('treo');
            }
            if (menu.label.includes('Manage Account Team')) {
                this.claimsService.ntrf = '';
            }
            if (menu.label.includes('Other Resources')) {
                this.loaderService.setItem('helpsTab', 'other');
                this.SharedService.setHelpsTab('other');
            }
            if (menu.label.includes('Change Account')) {
                this.loaderService.removeItem('orgId');
                this.accountHomeTitle = '';
                this.listOfMenuData = [];
                if (!location.pathname.includes('landing-home')) {
                    this.fetchNotificationCountOnLogo();
                }
                this.headerLinkToggle();
            }
            this.router.navigate([link]);
        }
        this.SharedService.setCustomizedLink(menu?.label);
    }
    checkMenuLink(menu) {
        return menu.link.includes('/dashboard/panel-management') || menu.link.includes('/claims/panel-management')
            ? '/claims/panel-management'
            : menu.link;
    }

    /**
     * toggle the mobile menu
     */
    toggleMobileMenu() {
        this.showMobileMenuOptions = !this.showMobileMenuOptions;
    }
    /**
     * toggle the mobile dropdown
     * @param val
     */
    toggleMobileDropDown(val: string) {
        if (this.mobileDropDown === val) {
            this.mobileDropDown = '';
        } else {
            this.mobileDropDown = val;
        }
    }
    /**
     * is the mobile dropdown open
     * @param val
     * @returns
     */
    isMobileDropDownOpen(val: string): boolean {
        return this.showMobileMenuOptions && this.mobileDropDown === val;
    }
    /**
     * stub method to be overridden by application class methods
     */
    logout() {
        this.navigateUrl();
    }
    /**
     * navigate to the logo link
     */
    navigateLogoLink(): void {
        this.loaderService.removeItem('orgId');
        // this.loaderService.removeItem('navigableLinks');
        this.accountHomeTitle = '';
        this.listOfMenuData = [];
        if (!this.headerConfig.disableLogoLink) {
            if (this.headerConfig.logoRouterLink) {
                this.router.navigate([this.headerConfig.logoRouterLink]);
            } else {
                this.navigateUrl();
            }
        }
        if (!location.pathname.includes('landing-home')) {
            this.fetchNotificationCountOnLogo();
        }
        this.headerLinkToggle();
    }

    headerLinkToggle() {
        /* istanbul ignore next */
        this.routingSubscription$ = this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe((routeData: any) => {
                this.headerLinkToggleIfBlock(routeData);
            });
    }

    headerLinkToggleIfBlock(routeData) {
        if (
            routeData.urlAfterRedirects === '/error' ||
            routeData.urlAfterRedirects === '/landing-home' ||
            routeData.urlAfterRedirects === '/login' ||
            routeData.urlAfterRedirects === '/sim-login' ||
            routeData.urlAfterRedirects === '/auth' ||
            routeData.urlAfterRedirects === '/no-cav-access' ||
            routeData.urlAfterRedirects === '/expired-user' ||
            routeData.urlAfterRedirects === '/dashboard/print-dashboard' ||
            routeData.urlAfterRedirects === '/no-user-auth' ||
            routeData.urlAfterRedirects === '/download-report' ||
            routeData.urlAfterRedirects === '/login-failed' ||
            routeData.urlAfterRedirects === '/unauthorized-access' ||
            routeData.urlAfterRedirects === '/timeout-error' ||
            routeData.urlAfterRedirects.includes('/reports-preview')
        ) {
            this.headerConfig.secondryNav = [];
            this.headerConfig.primaryNavLeft[0].label = '';
            if (this.headerConfig.primaryNavRight[0]?.subHeaderDetails)
                this.headerConfig.primaryNavRight[0].subHeaderDetails =
                    this.headerConfig.primaryNavRight[0]?.subHeaderDetails.filter(
                        (item) => typeof item !== 'object' || JSON.stringify(item) !== JSON.stringify(this.manageAlerts)
                    );

            if (this.headerConfig.primaryNavRight[2]?.subHeaderDetails)
                this.headerConfig.primaryNavRight[2].subHeaderDetails =
                    this.headerConfig.primaryNavRight[2]?.subHeaderDetails.filter(
                        (item) => typeof item !== 'object' || JSON.stringify(item) !== JSON.stringify(this.accountTeam)
                    );
            if (this.headerConfig.primaryNavRight[2]?.subHeaderDetails)
                this.headerConfig.primaryNavRight[2].subHeaderDetails =
                    this.headerConfig.primaryNavRight[2]?.subHeaderDetails.filter(
                        (item) =>
                            typeof item !== 'object' || JSON.stringify(item) !== JSON.stringify(this.changeAccDetails)
                    );

            if (this.externalUserCheck == true && this.headerConfig.primaryNavRight.length > 0) {
                this.headerConfig.primaryNavRight[2].subHeaderDetails[0] = this.EditProfileDetails;
            }
            if (this.externalUserCheck == false && this.headerConfig.primaryNavRight.length > 0) {
                this.headerConfig.primaryNavRight[2].subHeaderDetails[0];
            }
            if (this.headerConfig.primaryNavRight?.length === 2) {
                this.headerConfig.primaryNavRight[2].subHeaderDetails =
                    this.headerConfig.primaryNavRight[2]?.subHeaderDetails.filter(
                        (obj, index, self) =>
                            index === self.findIndex((o) => o.label === obj.label && o.link === obj.link)
                    );
            }
        } else if (location.pathname.includes('report-preview')) {
            this.headerConfig.primaryNavLeft[0].label = '';
            this.headerConfig.primaryNavRight = [];
        } else {
            this.headerLinkToggleElseBlock(routeData);
        }
    }

    markAccountAsVisited() {
        const appAccountDetailId = this.loaderService.getItem('appAccountDetailId') || 0;
        let payload = {
            accountDetailType: 'LAST_ACCESS',
            account: {
                organizationId: this.loaderService.getItem('orgId'),
                appAccountDetailId: appAccountDetailId,
                userId: this.loaderService.getItem('userId'),
                applctnAccntId: this.loaderService.getItem('applctnUserId')
            }
        };
        this.accountService.lastVisitedAccount(payload).subscribe({
            next: (resp) => {
                let orgArray = [];
                orgArray.push(this.loaderService.getItem('orgId'));
                this.fetchHeaderTabs(this.loaderService.getItem('orgId'), 'report');
            },
            error: (error) => {}
        });
    }
    headerLinkToggleElseBlock(routeData?: any) {
        if (!this.accountHomeTitle) {
            if (
                this.loaderService.getItem('accountName') == null ||
                this.loaderService.getItem('accountName') == 'null' ||
                this.loaderService.getItem('accountName') == '' ||
                this.loaderService.getLocalItem('accountName') === null ||
                this.loaderService.getLocalItem('accountName') === 'null' ||
                this.loaderService.getLocalItem('accountName') === ''
            ) {
                this.accountHomeTitle = '';
            } else if (
                this.loaderService.getLocalItem('accountName') !== null &&
                this.loaderService.getLocalItem('accountName') !== 'null' &&
                this.loaderService.getLocalItem('accountName') !== ''
            ) {
                this.accountHomeTitle = this.loaderService.getLocalItem('accountName');
            } else {
                this.accountHomeTitle = this.loaderService.getItem('accountName');
            }
        }
        this.headerConfig.primaryNavLeft[0].label = this.accountHomeTitle;
        if (this.listOfMenuData?.length === 0 || this.listOfMenuData == '') {
            if (sessionStorage.getItem('navigableLinks'))
                this.listOfMenuData = JSON.parse(sessionStorage.getItem('navigableLinks'));
        }
        if (
            this.accountHomeTitle == '' &&
            (routeData.urlAfterRedirects === '/notifications' ||
                routeData.urlAfterRedirects == '/toolsAndTrainingTab' ||
                routeData.urlAfterRedirects == '/announcements' ||
                routeData.urlAfterRedirects == '/contact' ||
                routeData.urlAfterRedirects == '/accountteam' ||
                routeData.urlAfterRedirects == '/accountteamsearchresults' ||
                routeData.urlAfterRedirects == '/accountteamdetails' ||
                routeData.urlAfterRedirects == '/accountteamdetailsaddaccount' ||
                routeData.urlAfterRedirects == '/profile-update')
        ) {
            this.headerConfig.secondryNav = [];
        } else {
            this.headerConfig.secondryNav = this.listOfMenuData;
        }
        if (
            !this.headerConfig.primaryNavRight[0]?.subHeaderDetails.some(
                (item) => JSON.stringify(item) === JSON.stringify(this.manageAlerts)
            ) &&
            this.accountHomeTitle !== ''
        ) {
            this.headerConfig.primaryNavRight[0]?.subHeaderDetails.splice(1, 0, this.manageAlerts);
        }
        if (
            !this.headerConfig.primaryNavRight[2]?.subHeaderDetails.some(
                (item) => JSON.stringify(item) === JSON.stringify(this.accountTeam)
            ) &&
            this.accountHomeTitle !== ''
        ) {
            this.headerConfig.primaryNavRight[2]?.subHeaderDetails.splice(0, 0, this.accountTeam);
        }
        if (
            !this.headerConfig.primaryNavRight[2]?.subHeaderDetails.some(
                (item) => JSON.stringify(item) === JSON.stringify(this.changeAccDetails)
            )
        ) {
            if (this.accountHomeTitle == '') {
                this.headerConfig.primaryNavRight[2]?.subHeaderDetails.splice(1, 0, this.changeAccDetails);
                this.headerConfig.primaryNavRight[2]?.subHeaderDetails;
            } else {
                this.headerConfig.primaryNavRight[2]?.subHeaderDetails.splice(2, 0, this.changeAccDetails);
                this.headerConfig.primaryNavRight[2]?.subHeaderDetails;
            }
            if (this.externalUserCheck == true && this.headerConfig.primaryNavRight.length > 0) {
                this.headerConfig.primaryNavRight[2].subHeaderDetails[1] = this.EditProfileDetails;
            }
            if (this.externalUserCheck == true && Number(this.loaderService.getItem('accountsLengthExternal')) === 1) {
                if (this.accountHomeTitle !== '') {
                    this.headerConfig.primaryNavRight[2]?.subHeaderDetails.splice(2, 1);
                    this.headerConfig.primaryNavRight[2]?.subHeaderDetails;
                } else {
                    this.headerConfig.primaryNavRight[2]?.subHeaderDetails.splice(0, 1);
                    this.headerConfig.primaryNavRight[2]?.subHeaderDetails;
                }
            }
            if (this.externalUserCheck == true && Number(this.loaderService.getItem('accountsLengthExternal')) > 1) {
                // Remove Change Account from subHeaderDetails
                const changeAccountIndex = this.headerConfig.primaryNavRight[2]?.subHeaderDetails.findIndex(
                    (item) => item.label === 'Change Account'
                );
                if (changeAccountIndex !== -1) {
                    this.headerConfig.primaryNavRight[2]?.subHeaderDetails.splice(
                        changeAccountIndex,
                        1,
                        this.changeAccDetails
                    );
                }
                // this.headerConfig.primaryNavRight[2]?.subHeaderDetails.splice(0, 1, this.changeAccDetails);
                // this.headerConfig.primaryNavRight[2]?.subHeaderDetails
            }
            if (this.externalUserCheck == false && this.headerConfig.primaryNavRight.length > 0) {
                this.headerConfig.primaryNavRight[2].subHeaderDetails[0];
            }
            if (this.headerConfig.primaryNavRight.length > 0) {
                this.headerConfig.primaryNavRight[2].subHeaderDetails =
                    this.headerConfig?.primaryNavRight[2]?.subHeaderDetails.filter(
                        (obj, index, self) =>
                            index === self.findIndex((o) => o.label === obj.label && o.link === obj.link)
                    );
            }

            if (!this.accountHomeTitle && this.alertMail.length > 0 && this.alertNavigate === 'Manage Alerts') {
                this.accountHomeTitle = this.alertMail;
            }
        }
    }

    alertMail = '';
    alertNavigate = '';

    downloadLocationExcel() {
        const payload = {
            orgnztnId: this.loaderService.getItem('orgId'),
            reportFormat: 'EXCEL',
            reportType: 'LOCATION_CHANGE_LOG'
        };
        this.excelService.generateExcelReport(
            `${CLAIM_API_ENDPOINTS.LOCATION_CHANGE_EXCEL}`,
            payload,
            'lctn_code_chg_rprt.xls'
        );
    }

    ngOnDestroy() {
        if (this.accountNameSubscription$) this.accountNameSubscription$.unsubscribe();
        if (this.orgnztnIdSubscription$) this.orgnztnIdSubscription$.unsubscribe();
        if (this.menuSubscription$) this.menuSubscription$.unsubscribe();
        if (this.navigationSubscription$) this.navigationSubscription$.unsubscribe();
        if (this.routingSubscription$) this.routingSubscription$.unsubscribe();
        if (this.routeSubscription$) this.routeSubscription$.unsubscribe();
        if (this.headerTabsSubscription$) this.headerTabsSubscription$.unsubscribe();
    }
    policyTabSubscription$: Subscription;
    notificationSubscription$: Subscription;
    fetchPolicyList(orgIdArray) {
        let userId = JSON.parse(localStorage.getItem('userId'));
        let url = '';
        if (this.loaderService.getItem('appAccountDetailId') === '0') {
            url = `${ACCOUNTS_API_ENDPOINTS.SEARCH}?accountIdentifier=${orgIdArray[0]}&accountIdentifierType=PolicyList&userId=${userId}`;
        } else {
            url = `${ACCOUNTS_API_ENDPOINTS.SEARCH}?accountIdentifier=${
                orgIdArray[0]
            }&accountIdentifierOption=${this.loaderService.getItem(
                'appAccountDetailId'
            )}&accountIdentifierType=PolicyList&userId=${userId}`;
        }
        let payload = {
            accountIdentifier: orgIdArray[0],
            accountIdentifierType: 'PolicyList',
            accountIdentifierOption: this.loaderService.getItem('appAccountDetailId')
        };
        this.policyTabSubscription$ = this.apiService.getData1(url, payload).subscribe({
            next: (resp) => {
                this.SharedService.setPolicyListData(resp.policyList);
                this.loaderService.setItem('policyList', JSON.stringify(resp.policyList));
            },
            error: (error) => {}
        });
    }
    fetchNotificationCount(orgIdArray) {
        let userId = this.loaderService.getItem('userId');
        let url = '';
        if (this.loaderService.getItem('appAccountDetailId') === '0') {
            url = `${ACCOUNTS_API_ENDPOINTS.SEARCH}?accountIdentifier=${orgIdArray[0]}&accountIdentifierType=AccountNotVisitedCount&userId=${userId}`;
        } else {
            url = `${ACCOUNTS_API_ENDPOINTS.SEARCH}?accountIdentifier=${
                orgIdArray[0]
            }&accountIdentifierOption=${this.loaderService.getItem(
                'appAccountDetailId'
            )}&accountIdentifierType=AccountNotVisitedCount&userId=${userId}`;
        }
        let payload = {
            accountIdentifier: orgIdArray[0],
            accountIdentifierType: 'AccountNotVisitedCount'
        };
        this.notificationSubscription$ = this.apiService.getData1(url, payload).subscribe({
            next: (resp) => {
                this.loaderService.setItem('accountNotVisitedCount', resp.accountNotVisitedCount);
                this.SharedService.getAlertCountInside();
            },
            error: (error) => {}
        });
    }

    fetchNotificationCountOnLogo() {
        const userId = this.loaderService.getItem('userId');
        let url = `${ACCOUNTS_API_ENDPOINTS.SEARCH}?accountIdentifier=null&accountIdentifierType=AccountNotVisitedCount&userId=${userId}`;
        let payload = {
            accountIdentifier: 'null',
            accountIdentifierType: 'AccountNotVisitedCount'
        };
        this.notificationSubscription$ = this.apiService.getData1(url, payload).subscribe({
            next: (resp) => {
                this.loaderService.setItem('accountNotVisitedCount', resp.accountNotVisitedCount);
                this.SharedService.updateAlertCountInside(resp.accountNotVisitedCount);
            },
            error: (error) => {
                console.error('Notification fetch failed', error);
            }
        });
    }
}
